import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Search } from 'lucide-react'

import { WorkflowFlowMember, WorkflowStepDraft } from './types'

interface StepReviewerPickerProps {
  step: WorkflowStepDraft
  members: WorkflowFlowMember[]
  onToggleReviewer: (memberId: string) => void
}

/**
 * Searchable reviewer checklist for ONE step — moved verbatim out of the old
 * form-list WorkflowStepsEditor, where it was rendered once per step row. The
 * flow editor renders exactly one of these at a time, in the side panel of the
 * currently selected step node.
 */
const StepReviewerPicker: React.FC<StepReviewerPickerProps> = ({ step, members, onToggleReviewer }) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return members
    return members.filter(
      (m) => m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q),
    )
  }, [members, search])

  return (
    <div>
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t('Submittals_submit_for_review_search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 pl-8 text-sm"
        />
      </div>

      <div className="max-h-44 space-y-0.5 overflow-y-auto rounded-md border p-1.5">
        {filteredMembers.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {t('Submittals_submit_for_review_no_members')}
          </p>
        ) : (
          filteredMembers.map((member) => (
            <div
              key={member.id}
              className="flex items-center gap-2.5 rounded px-2 py-1.5 hover:bg-muted"
            >
              <Checkbox
                id={`wf-step-${step.key}-reviewer-${member.id}`}
                checked={step.reviewerIds.includes(member.id)}
                onCheckedChange={() => onToggleReviewer(member.id)}
              />
              <Label
                htmlFor={`wf-step-${step.key}-reviewer-${member.id}`}
                className="flex-1 cursor-pointer text-sm font-normal"
              >
                {member.name}
                {member.email && <span className="ml-1.5 text-muted-foreground">{member.email}</span>}
              </Label>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default StepReviewerPicker
